import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  Chip,
  IconButton,
  LinearProgress,
  Badge,
  Button,
  Tooltip,
  Stack,
  Paper,
  Alert,
  alpha,
} from "@mui/material";
import {
  Inbox as InboxIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  MarkEmailRead as MarkReadIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  EditNote as ComposeIcon,
  Queue as QueueIcon,
  School as SchoolIcon,
  ArrowBack as ArrowBackIcon,
  Forum as ForumIcon,
  MarkEmailUnread as UnreadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import whatsappApiService from "../services/whatsappApiService";
import PageContentLayout from "../components/layout/PageContentLayout";
import { useWhatsAppEvents } from "../contexts/WhatsAppEventsContext";

const WA_GREEN = "#25D366";
const WA_DARK = "#128C7E";
const WA_CHAT_BG = "#efeae2";
const WA_BUBBLE_OUT = "#d9fdd3";
const WA_BUBBLE_IN = "#ffffff";
const SESSION_MS = 24 * 60 * 60 * 1000;

function computeMessagingWindowFromThread(thread) {
  const inbound = (thread || []).filter((m) => m.direction === "inbound" && m.messageAt);
  if (!inbound.length) {
    return {
      within24h: false,
      lastInboundAt: null,
      expiresAt: null,
      minutesRemaining: 0,
    };
  }

  const lastMs = Math.max(...inbound.map((m) => new Date(m.messageAt).getTime()));
  const expiresMs = lastMs + SESSION_MS;
  const within24h = Date.now() < expiresMs;

  return {
    within24h,
    lastInboundAt: new Date(lastMs).toISOString(),
    expiresAt: new Date(expiresMs).toISOString(),
    minutesRemaining: within24h ? Math.floor((expiresMs - Date.now()) / 60000) : 0,
  };
}

function formatWindowRemaining(minutes) {
  if (!minutes || minutes <= 0) return "";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

const NAV_ACTION_TONES = {
  compose: {
    main: WA_DARK,
    bg: alpha(WA_GREEN, 0.12),
    border: alpha(WA_GREEN, 0.32),
    hoverBg: alpha(WA_GREEN, 0.2),
  },
  queue: {
    main: "#ed6c02",
    bg: alpha("#ed6c02", 0.1),
    border: alpha("#ed6c02", 0.28),
    hoverBg: alpha("#ed6c02", 0.16),
  },
  unread: {
    main: "#e65100",
    bg: alpha("#ed6c02", 0.1),
    border: alpha("#ed6c02", 0.28),
    hoverBg: alpha("#ed6c02", 0.16),
  },
  refresh: {
    main: "#6a1b9a",
    bg: alpha("#6a1b9a", 0.1),
    border: alpha("#6a1b9a", 0.28),
    hoverBg: alpha("#6a1b9a", 0.16),
  },
};

const CHAT_WALLPAPER_SX = {
  bgcolor: WA_CHAT_BG,
  backgroundImage: `radial-gradient(${alpha(WA_DARK, 0.06)} 1px, transparent 1px)`,
  backgroundSize: "18px 18px",
};

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSchoolLabel(conversation) {
  const name = String(conversation?.schoolName || "").trim();
  const code = String(conversation?.schoolCode || "").trim();

  if (name && code) {
    if (name.toLowerCase().includes(code.toLowerCase())) {
      return name;
    }
    return `${name} (${code})`;
  }
  if (name) return name;
  if (code) return code;
  return conversation?.contactNumber || "Unknown";
}

function normalizeContact(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";

  let normalized = digits;
  if (normalized.length === 11 && normalized.startsWith("0")) {
    normalized = normalized.slice(1);
  }
  if (normalized.length === 10 && /^[6-9]/.test(normalized)) {
    return `91${normalized}`;
  }
  if (normalized.length === 12 && normalized.startsWith("91")) {
    return normalized;
  }
  if (normalized.length > 10) {
    const suffix = normalized.slice(-10);
    if (/^[6-9]/.test(suffix)) {
      return `91${suffix}`;
    }
  }
  return normalized;
}

function normalizePhone(value) {
  const contact = normalizeContact(value);
  return contact.length >= 10 ? contact.slice(-10) : contact;
}

function shouldShowPhoneChip(title, contact) {
  const phone = String(contact || "").trim();
  if (!phone) return false;

  const normalizedTitle = String(title || "").trim();
  if (normalizedTitle === phone) return false;
  if (normalizePhone(normalizedTitle) === normalizePhone(phone)) return false;

  return true;
}

function getAvatarLabel(conversation) {
  const name = formatSchoolLabel(conversation);
  const letter = name.replace(/[^a-zA-Z0-9]/g, "").charAt(0);
  return (letter || "#").toUpperCase();
}

const NavActionButton = memo(function NavActionButton({
  icon,
  label,
  onClick,
  disabled,
  tone,
  active,
}) {
  const palette = NAV_ACTION_TONES[tone];

  return (
    <Button
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={{
        textTransform: "none",
        borderRadius: 2,
        px: 1.5,
        py: 0.65,
        minHeight: 36,
        fontWeight: 600,
        fontSize: "0.8125rem",
        color: palette.main,
        bgcolor: active ? palette.hoverBg : palette.bg,
        border: `1px solid ${active ? palette.main : palette.border}`,
        boxShadow: active
          ? `0 2px 8px ${alpha(palette.main, 0.2)}`
          : `0 1px 3px ${alpha(palette.main, 0.12)}`,
        gap: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          bgcolor: palette.hoverBg,
          borderColor: palette.main,
          boxShadow: `0 4px 14px ${alpha(palette.main, 0.22)}`,
          transform: "translateY(-1px)",
        },
        "&.Mui-disabled": {
          color: alpha(palette.main, 0.45),
          bgcolor: alpha(palette.main, 0.05),
          borderColor: alpha(palette.main, 0.15),
        },
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(palette.main, 0.14),
          color: palette.main,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      {label}
    </Button>
  );
});

const InboxStatChip = memo(function InboxStatChip({ label, value, color }) {
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.5,
        borderRadius: 1.5,
        bgcolor: alpha(color, 0.1),
        border: `1px solid ${alpha(color, 0.22)}`,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
});

const ConversationRow = memo(function ConversationRow({ conv, isSelected, onSelect }) {
  const name = formatSchoolLabel(conv);
  const contactKey = normalizeContact(conv.contactNumber);
  const hasUnread = conv.unreadCount > 0;
  const isSchool = Boolean(conv.schoolCode || conv.schoolName);

  return (
    <ListItemButton
      selected={isSelected}
      onClick={() => onSelect(conv.contactNumber, conv)}
      sx={{
        px: 1.5,
        py: 1.25,
        gap: 1.25,
        alignItems: "flex-start",
        borderBottom: `1px solid ${alpha(WA_GREEN, 0.08)}`,
        transition: "background-color 0.15s ease",
        "&.Mui-selected": {
          bgcolor: alpha(WA_GREEN, 0.12),
          borderLeft: `3px solid ${WA_GREEN}`,
          "&:hover": { bgcolor: alpha(WA_GREEN, 0.14) },
        },
        "&:hover": { bgcolor: alpha(WA_GREEN, 0.06) },
      }}
    >
      <Badge
        overlap="circular"
        variant="dot"
        invisible={!hasUnread}
        sx={{
          "& .MuiBadge-badge": {
            bgcolor: "#ed6c02",
            boxShadow: `0 0 0 2px #fff`,
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: isSchool ? alpha(WA_DARK, 0.12) : alpha(WA_GREEN, 0.14),
            color: isSchool ? WA_DARK : WA_GREEN,
            fontWeight: 700,
            fontSize: "0.9375rem",
          }}
        >
          {isSchool ? <SchoolIcon sx={{ fontSize: 22 }} /> : getAvatarLabel(conv)}
        </Box>
      </Badge>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.35 }}>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ fontWeight: hasUnread ? 700 : 600, fontSize: "0.8125rem" }}
          >
            {name}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              color: hasUnread ? WA_DARK : "text.secondary",
              fontWeight: hasUnread ? 700 : 400,
              flexShrink: 0,
            }}
          >
            {formatTime(conv.lastMessageAt)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: 1,
              fontSize: "0.8125rem",
              color: hasUnread ? "text.primary" : "text.secondary",
              fontWeight: hasUnread ? 600 : 400,
            }}
          >
            {conv.lastMessage || "—"}
          </Typography>
          {hasUnread && (
            <Box
              sx={{
                minWidth: 20,
                height: 20,
                px: 0.5,
                borderRadius: 10,
                bgcolor: "#ed6c02",
                color: "#fff",
                fontSize: "0.6875rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {conv.unreadCount > 99 ? "99+" : conv.unreadCount}
            </Box>
          )}
        </Box>
      </Box>
    </ListItemButton>
  );
});

const WhatsAppSessionBanner = memo(function WhatsAppSessionBanner({ messagingWindow }) {
  if (!messagingWindow) return null;

  if (messagingWindow.within24h) {
    const remaining = formatWindowRemaining(messagingWindow.minutesRemaining);
    return (
      <Alert
        severity="success"
        icon={<CheckCircleIcon fontSize="inherit" />}
        sx={{
          mx: 1.5,
          mt: 1,
          py: 0.25,
          fontSize: "0.8125rem",
          "& .MuiAlert-message": { width: "100%" },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
          24-hour window open
        </Typography>
        <Typography variant="caption" color="text.secondary">
          User messaged you recently — you can send normal text replies
          {remaining ? ` · closes in ${remaining}` : ""}.
        </Typography>
      </Alert>
    );
  }

  return (
    <Alert
      severity="warning"
      icon={<WarningIcon fontSize="inherit" />}
      sx={{
        mx: 1.5,
        mt: 1,
        py: 0.25,
        fontSize: "0.8125rem",
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
        Outside 24-hour window
      </Typography>
      <Typography variant="caption" color="text.secondary">
        User has not messaged in the last 24 hours — you can read this conversation, but replying is not available here. Use Compose to send an approved template.
      </Typography>
    </Alert>
  );
});

const MessageBubble = memo(function MessageBubble({ msg }) {
  const isOutbound = msg.direction === "outbound";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isOutbound ? "flex-end" : "flex-start",
        mb: 1,
        px: 0.5,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: "78%",
          px: 1.5,
          py: 1,
          borderRadius: isOutbound ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
          bgcolor: isOutbound ? WA_BUBBLE_OUT : WA_BUBBLE_IN,
          border: `1px solid ${alpha(isOutbound ? WA_GREEN : "#64748b", isOutbound ? 0.25 : 0.12)}`,
          boxShadow: `0 1px 2px ${alpha("#000", 0.06)}`,
        }}
      >
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", lineHeight: 1.45, fontSize: "0.875rem" }}
        >
          {msg.message}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 0.75,
            mt: 0.5,
          }}
        >
          {msg.status && isOutbound && (
            <Chip
              label={msg.status}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.625rem",
                fontWeight: 700,
                textTransform: "capitalize",
                bgcolor: alpha(WA_DARK, 0.1),
                color: WA_DARK,
                border: `1px solid ${alpha(WA_DARK, 0.2)}`,
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          )}
          <Typography variant="caption" sx={{ color: alpha("#000", 0.45), fontSize: "0.6875rem" }}>
            {formatTime(msg.messageAt)}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
});

const WhatsAppInbox = () => {
  const navigate = useNavigate();
  const threadEndRef = useRef(null);
  const lastHandledEventIdRef = useRef(0);
  const { connectionState, stats: liveStats, lastInboxEvent } = useWhatsAppEvents();

  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({
    totalMessages: 0,
    unreadMessages: 0,
    conversations: 0,
    unreadConversations: 0,
  });
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedMeta, setSelectedMeta] = useState(null);
  const [thread, setThread] = useState([]);
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [messagingWindow, setMessagingWindow] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingList(true);
    try {
      const result = await whatsappApiService.getInboxConversations({
        search: search.trim(),
        unreadOnly,
        limit: 100,
      });

      if (result.success) {
        setConversations(result.data?.items ?? []);
        if (result.data?.stats) {
          setStats(result.data.stats);
        }
      } else if (!silent) {
        toast.error(result.error || "Failed to load inbox");
        setConversations([]);
      }
    } catch (error) {
      if (!silent) toast.error("Failed to load inbox: " + error.message);
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [search, unreadOnly]);

  const loadThread = useCallback(
    async (contact, meta, { silent = false } = {}) => {
      const normalizedContact = normalizeContact(contact);
      if (!normalizedContact) return;
      if (!silent) setLoadingThread(true);
      try {
        const result = await whatsappApiService.getInboxThread(normalizedContact);
        if (result.success) {
          const items = result.data?.items ?? [];
          setThread(items);
          setMessagingWindow(
            result.data?.messagingWindow ?? computeMessagingWindowFromThread(items)
          );
          setSelectedContact(normalizedContact);
          setSelectedMeta(meta ? { ...meta, contactNumber: normalizedContact } : null);
          setReplyText("");
          await loadConversations({ silent });
        } else if (!silent) {
          toast.error(result.error || "Failed to load conversation");
        }
      } catch (error) {
        if (!silent) toast.error("Failed to load conversation: " + error.message);
      } finally {
        if (!silent) setLoadingThread(false);
      }
    },
    [loadConversations]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      loadConversations();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedContact) {
      setMessagingWindow(null);
      return;
    }
    if (thread.length > 0) {
      setMessagingWindow(computeMessagingWindowFromThread(thread));
    }
  }, [selectedContact, thread]);

  useEffect(() => {
    if (liveStats) {
      setStats(liveStats);
    }
  }, [liveStats]);

  useEffect(() => {
    if (!lastInboxEvent?.id || lastInboxEvent.id <= lastHandledEventIdRef.current) {
      return;
    }
    lastHandledEventIdRef.current = lastInboxEvent.id;

    loadConversations({ silent: true });

    const eventContact = normalizeContact(lastInboxEvent.contactNumber);
    const activeContact = normalizeContact(selectedContact);
    if (eventContact && activeContact && eventContact === activeContact) {
      loadThread(selectedContact, selectedMeta, { silent: true });
    }
  }, [lastInboxEvent, selectedContact, selectedMeta, loadConversations, loadThread]);

  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [thread]);

  const canReply = messagingWindow?.within24h ?? false;
  const canSendReply = canReply && Boolean(replyText.trim());

  const handleSendReply = async () => {
    if (!selectedContact) return;

    if (!canReply) {
      toast.error("Reply is only available when the user messaged you within the last 24 hours");
      return;
    }
    if (!replyText.trim()) {
      toast.error("Enter a message to send");
      return;
    }

    setSending(true);
    try {
      const result = await whatsappApiService.sendInboxReply(
        selectedContact,
        replyText.trim(),
        selectedMeta?.schoolCode || null
      );

      if (result.success) {
        toast.success("Reply sent");
        setReplyText("");
        await loadThread(selectedContact, selectedMeta);
      } else {
        toast.error(result.error || "Failed to send reply");
      }
    } catch (error) {
      toast.error("Failed to send reply: " + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleMarkAllRead = async () => {
    if (!selectedContact) return;
    const result = await whatsappApiService.markInboxRead({ contact: selectedContact });
    if (result.success) {
      toast.success("Marked as read");
      await loadConversations();
      if (selectedContact) {
        await loadThread(selectedContact, selectedMeta);
      }
    }
  };

  const handleRefresh = () => {
    loadConversations();
    if (selectedContact) loadThread(selectedContact, selectedMeta);
  };

  const threadTitle = useMemo(() => {
    if (!selectedContact) return "";

    const meta = selectedMeta || { contactNumber: selectedContact };
    const fromThread = thread.reduce(
      (acc, msg) => ({
        schoolName: acc.schoolName || msg.schoolName,
        schoolCode: acc.schoolCode || msg.schoolCode,
      }),
      { schoolName: null, schoolCode: null }
    );

    return formatSchoolLabel({
      ...fromThread,
      ...meta,
      contactNumber: selectedContact,
    });
  }, [selectedContact, selectedMeta, thread]);

  const unreadLabel = stats.unreadConversations > 0 ? `Unread (${stats.unreadConversations})` : "Unread";

  const inboxBarTitle = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        WhatsApp Inbox
      </Typography>
      <Tooltip
        title={
          connectionState === "open"
            ? "Live updates connected"
            : connectionState === "connecting"
              ? "Connecting live updates…"
              : "Live updates reconnecting…"
        }
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor:
              connectionState === "open"
                ? WA_GREEN
                : connectionState === "connecting"
                  ? "#ed6c02"
                  : "#d32f2f",
            boxShadow:
              connectionState === "open" ? `0 0 0 3px ${alpha(WA_GREEN, 0.25)}` : "none",
          }}
        />
      </Tooltip>
    </Box>
  );

  return (
    <PageContentLayout
      title="WhatsApp Inbox"
      subtitle={`${stats.conversations} chats · ${stats.unreadConversations} unread`}
      barTitle={inboxBarTitle}
      barSubtitle={`${stats.conversations} chats · ${stats.unreadConversations} unread`}
      breadcrumbs={[
        {
          label: "WhatsApp Inbox",
          icon: <InboxIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/whatsapp"
      bareContent
      cardSx={{
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: "calc(100vh - 200px)",
      }}
      actions={
        <>
          <NavActionButton
            tone="compose"
            label="Compose"
            icon={<ComposeIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate("/whatsapp")}
          />
          <NavActionButton
            tone="queue"
            label="Queue"
            icon={<QueueIcon sx={{ fontSize: 16 }} />}
            onClick={() => navigate("/whatsapp/queue")}
          />
          <NavActionButton
            tone="unread"
            label={unreadLabel}
            icon={<UnreadIcon sx={{ fontSize: 16 }} />}
            onClick={() => setUnreadOnly((v) => !v)}
            active={unreadOnly}
          />
          <NavActionButton
            tone="refresh"
            label="Refresh"
            icon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={handleRefresh}
            disabled={loadingList || loadingThread}
          />
        </>
      }
    >
          <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
            {/* Conversation list */}
            <Box
              sx={{
                width: { xs: "100%", md: 380 },
                borderRight: `1px solid ${alpha(WA_GREEN, 0.15)}`,
                display: { xs: selectedContact ? "none" : "flex", md: "flex" },
                flexDirection: "column",
                bgcolor: "#fff",
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: `1px solid ${alpha(WA_GREEN, 0.12)}`,
                  bgcolor: alpha(WA_GREEN, 0.04),
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search contact, school, message..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#fff",
                      fontSize: "0.875rem",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: WA_DARK }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
                  <InboxStatChip label="chats" value={stats.conversations} color={WA_DARK} />
                  <InboxStatChip
                    label="unread"
                    value={stats.unreadConversations}
                    color="#ed6c02"
                  />
                  <InboxStatChip label="messages" value={stats.totalMessages} color="#64748b" />
                </Stack>
              </Box>

              {loadingList && <LinearProgress color="success" sx={{ flexShrink: 0 }} />}

              <List sx={{ flex: 1, overflow: "auto", py: 0 }}>
                {conversations.length === 0 && !loadingList ? (
                  <Box sx={{ p: 5, textAlign: "center" }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        mx: "auto",
                        mb: 1.5,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: alpha(WA_GREEN, 0.1),
                      }}
                    >
                      <ForumIcon sx={{ fontSize: 32, color: alpha(WA_GREEN, 0.55) }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      No conversations yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 260, mx: "auto" }}>
                      Replies from schools appear here via the Meta webhook
                    </Typography>
                  </Box>
                ) : (
                  conversations.map((conv) => {
                    const contactKey = normalizeContact(conv.contactNumber);
                    const isSelected = normalizeContact(selectedContact) === contactKey;
                    return (
                      <ConversationRow
                        key={contactKey || conv.contactNumber}
                        conv={conv}
                        isSelected={isSelected}
                        onSelect={loadThread}
                      />
                    );
                  })
                )}
              </List>
            </Box>

            {/* Thread panel */}
            <Box
              sx={{
                flex: 1,
                display: { xs: selectedContact ? "flex" : "none", md: "flex" },
                flexDirection: "column",
                minWidth: 0,
                bgcolor: "#fff",
              }}
            >
              {!selectedContact ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    p: 4,
                    ...CHAT_WALLPAPER_SX,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      mb: 2,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(WA_GREEN, 0.12),
                      border: `2px solid ${alpha(WA_GREEN, 0.25)}`,
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 40, color: alpha(WA_GREEN, 0.7) }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: WA_DARK }}>
                    Select a conversation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center", maxWidth: 320 }}>
                    Incoming WhatsApp replies are recorded automatically. Pick a chat to view the conversation — you can reply only if they messaged within the last 24 hours.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderBottom: `1px solid ${alpha(WA_GREEN, 0.15)}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      flexWrap: "wrap",
                      bgcolor: alpha(WA_GREEN, 0.06),
                      flexShrink: 0,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedContact(null);
                          setSelectedMeta(null);
                          setThread([]);
                        }}
                        sx={{
                          display: { xs: "inline-flex", md: "none" },
                          bgcolor: alpha(WA_GREEN, 0.1),
                        }}
                        aria-label="Back to list"
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          display: { xs: "none", sm: "flex" },
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(WA_DARK, 0.12),
                          color: WA_DARK,
                          flexShrink: 0,
                        }}
                      >
                        <SchoolIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {threadTitle}
                        </Typography>
                        {shouldShowPhoneChip(threadTitle, selectedContact) && (
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              mt: 0.35,
                              px: 0.75,
                              py: 0.2,
                              borderRadius: 1,
                              bgcolor: alpha(WA_GREEN, 0.1),
                              border: `1px solid ${alpha(WA_GREEN, 0.2)}`,
                            }}
                          >
                            <PhoneIcon sx={{ fontSize: 12, color: WA_DARK }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontFamily: "ui-monospace, monospace",
                                fontWeight: 600,
                                color: WA_DARK,
                              }}
                            >
                              {selectedContact}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      startIcon={<MarkReadIcon sx={{ fontSize: 16 }} />}
                      onClick={handleMarkAllRead}
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        color: WA_DARK,
                        borderColor: alpha(WA_DARK, 0.3),
                      }}
                      variant="outlined"
                    >
                      Mark read
                    </Button>
                  </Box>

                  {loadingThread && <LinearProgress color="success" sx={{ flexShrink: 0 }} />}

                  <WhatsAppSessionBanner messagingWindow={messagingWindow} />

                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      px: 2,
                      py: 2,
                      ...CHAT_WALLPAPER_SX,
                    }}
                  >
                    {thread.length === 0 && !loadingThread ? (
                      <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No messages in this thread yet
                        </Typography>
                      </Box>
                    ) : (
                      thread.map((msg, index) => (
                        <MessageBubble
                          key={`${msg.direction}-${msg.id}-${index}`}
                          msg={msg}
                        />
                      ))
                    )}
                    <div ref={threadEndRef} />
                  </Box>

                  <Box
                    sx={{
                      p: 1.5,
                      borderTop: `1px solid ${alpha(WA_GREEN, 0.15)}`,
                      bgcolor: alpha(WA_GREEN, 0.04),
                      flexShrink: 0,
                    }}
                  >
                    {canReply ? (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                        <TextField
                          fullWidth
                          multiline
                          maxRows={4}
                          placeholder="Type a reply..."
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              handleSendReply();
                            }
                          }}
                          disabled={sending}
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2.5,
                              bgcolor: "#fff",
                              fontSize: "0.875rem",
                            },
                          }}
                        />
                        <Tooltip title="Send reply">
                          <span>
                            <IconButton
                              onClick={handleSendReply}
                              disabled={sending || !canSendReply}
                              sx={{
                                width: 44,
                                height: 44,
                                bgcolor: WA_GREEN,
                                color: "#fff",
                                boxShadow: `0 4px 12px ${alpha(WA_GREEN, 0.35)}`,
                                "&:hover": { bgcolor: WA_DARK },
                                "&.Mui-disabled": {
                                  bgcolor: alpha(WA_GREEN, 0.3),
                                  color: alpha("#fff", 0.7),
                                },
                              }}
                            >
                              <SendIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                          Reply unavailable — user has not messaged in the last 24 hours.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ComposeIcon sx={{ fontSize: 16 }} />}
                          onClick={() => navigate("/whatsapp")}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            color: WA_DARK,
                            borderColor: alpha(WA_DARK, 0.3),
                            flexShrink: 0,
                          }}
                        >
                          Send template via Compose
                        </Button>
                      </Stack>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
    </PageContentLayout>
  );
};

export default WhatsAppInbox;
